import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Leaf, MapPin, Mail, Phone, Facebook, Instagram, Youtube, ArrowUp, Lock } from 'lucide-react';
import { garden, navItems } from '../mock/mock';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Secret owner access: triple-click the footer leaf logo within 800ms -> /admin
  const secret = useRef({ count: 0, timer: null });
  const ownerAccess = () => {
    const s = secret.current;
    s.count += 1;
    clearTimeout(s.timer);
    s.timer = setTimeout(() => { s.count = 0; }, 800);
    if (s.count >= 3) { s.count = 0; navigate('/admin'); }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/subscribe`, { email });
      if (data.already_subscribed) {
        toast({ title: 'Already subscribed', description: `${email} is already on our list — thank you!` });
      } else {
        toast({ title: 'You are on the list!', description: `We'll keep ${email} posted as the garden grows.` });
      }
      setEmail('');
    } catch (err) {
      toast({ title: 'Please check your email', description: 'Enter a valid email address and try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-forest-dark text-cream/80">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span onClick={ownerAccess} title="" className="flex items-center justify-center w-11 h-11 rounded-full bg-forest-light select-none cursor-default">
                <Leaf className="w-6 h-6 text-gold-light" strokeWidth={1.6} />
              </span>
              <span className="leading-tight">
                <span className="block font-display text-base text-white">RODERICK GAY</span>
                <span className="block font-display text-[11px] tracking-[0.28em] text-gold-light">BOTANICAL GARDEN</span>
              </span>
            </div>
            <p className="text-sm leading-relaxed text-cream/70">Cultivating, conserving, displaying and celebrating plants while connecting people with nature.</p>
            <div className="flex gap-3 mt-6">
              {[Facebook, Instagram, Youtube].map((Icon, i) => (
                <a key={i} href="#" className="w-9 h-9 rounded-full border border-cream/25 flex items-center justify-center hover:bg-gold hover:border-gold transition-colors">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-display text-white text-lg mb-5">Explore</h4>
            <ul className="space-y-2.5 text-sm">
              {navItems.slice(1, 7).map((n) => (
                <li key={n.label}><Link to={n.to} className="hover:text-gold-light transition-colors">{n.label}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-white text-lg mb-5">Visit & Connect</h4>
            <ul className="space-y-3.5 text-sm">
              <li className="flex gap-3"><MapPin className="w-4 h-4 text-gold-light shrink-0 mt-0.5" /><span>{garden.address}</span></li>
              <li className="flex gap-3"><Phone className="w-4 h-4 text-gold-light shrink-0 mt-0.5" /><span>{garden.phone}</span></li>
              <li className="flex gap-3"><Mail className="w-4 h-4 text-gold-light shrink-0 mt-0.5" /><span>{garden.email}</span></li>
              <li className="flex gap-3"><Lock className="w-4 h-4 text-gold-light shrink-0 mt-0.5" /><Link to="/admin" className="hover:text-gold-light transition-colors">Owner Login</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-white text-lg mb-5">Stay in Bloom</h4>
            <p className="text-sm text-cream/70 mb-4">Join our newsletter for garden updates and opening news.</p>
            <form onSubmit={subscribe} className="flex flex-col gap-3">
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="Your email" className="w-full px-4 py-3 rounded-lg bg-forest text-cream placeholder:text-cream/50 border border-forest-light focus:outline-none focus:border-gold-light transition-colors text-sm" />
              <button type="submit" disabled={busy} className="px-5 py-3 rounded-lg bg-gold text-white font-600 text-sm hover:bg-gold-light transition-colors disabled:opacity-60">{busy ? 'Subscribing...' : 'Subscribe'}</button>
            </form>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-cream/15 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-cream/60">
          <p>&copy; {new Date().getFullYear()} {garden.name}. All rights reserved.</p>
          <Link to="/admin" className="flex items-center gap-2 text-cream/70 hover:text-gold-light transition-colors">
            <Lock className="w-3.5 h-3.5" /> Owner Login
          </Link>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2 hover:text-gold-light transition-colors">
            Back to top <ArrowUp className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
